import React, { useId } from 'react';
import styles from './NotificationBell.module.css';

/** Idle (no unread) vs attention (has unread tray items). */
export const NOTIFICATION_BELL_COLOR_IDLE = '#111827';

type NotificationBellIconProps = {
  size?: number;
  className?: string;
  title?: string;
  /** Metallic gold when true; black when false/undefined. */
  hasUnread?: boolean;
};

/**
 * Shared notification bell for broker / shipper / carrier headers.
 * Black when clear; metallic gold when there are unread bell notifications.
 */
export function NotificationBellIcon({
  size = 28,
  className,
  title = 'Notifications',
  hasUnread = false,
}: NotificationBellIconProps) {
  const decorative = !title;
  const uid = useId().replace(/:/g, '');
  const goldGradId = `bellGold-${uid}`;
  const goldShineId = `bellShine-${uid}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative ? true : undefined}
      aria-label={decorative ? undefined : title}
      style={{ display: 'block', shapeRendering: 'geometricPrecision' }}
    >
      {hasUnread ? (
        <defs>
          <linearGradient id={goldGradId} x1="5" y1="2" x2="19" y2="22" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#F8E7A0" />
            <stop offset="35%" stopColor="#E8C547" />
            <stop offset="70%" stopColor="#C9A227" />
            <stop offset="100%" stopColor="#8B6914" />
          </linearGradient>
          <radialGradient id={goldShineId} cx="35%" cy="28%" r="55%">
            <stop offset="0%" stopColor="#FFF8DC" stopOpacity="0.95" />
            <stop offset="45%" stopColor="#F0D56A" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#8B6914" stopOpacity="0" />
          </radialGradient>
        </defs>
      ) : null}

      <path
        d="M12 3.25c-2.98 0-5.4 2.3-5.4 5.14v2.05c0 .7-.2 1.38-.58 1.96L4.55 14.9c-.82 1.24.07 2.9 1.54 2.9h11.82c1.47 0 2.36-1.66 1.54-2.9l-1.47-2.5a3.5 3.5 0 0 1-.58-1.96V8.39c0-2.84-2.42-5.14-5.4-5.14Z"
        fill={hasUnread ? `url(#${goldGradId})` : NOTIFICATION_BELL_COLOR_IDLE}
        stroke={hasUnread ? '#7A5C10' : 'none'}
        strokeWidth={hasUnread ? 0.6 : 0}
      />
      {hasUnread ? (
        <path
          d="M12 3.25c-2.98 0-5.4 2.3-5.4 5.14v2.05c0 .7-.2 1.38-.58 1.96L4.55 14.9c-.82 1.24.07 2.9 1.54 2.9h11.82c1.47 0 2.36-1.66 1.54-2.9l-1.47-2.5a3.5 3.5 0 0 1-.58-1.96V8.39c0-2.84-2.42-5.14-5.4-5.14Z"
          fill={`url(#${goldShineId})`}
        />
      ) : null}
      <path
        d="M9.55 17.8a2.45 2.45 0 0 0 4.9 0"
        stroke={hasUnread ? `url(#${goldGradId})` : NOTIFICATION_BELL_COLOR_IDLE}
        strokeWidth="2.1"
        strokeLinecap="round"
      />
      {hasUnread ? (
        <path
          d="M9.55 17.8a2.45 2.45 0 0 0 4.9 0"
          stroke="#7A5C10"
          strokeWidth="0.7"
          strokeLinecap="round"
          opacity="0.55"
        />
      ) : null}
    </svg>
  );
}

type NotificationBellBadgeProps = {
  count: number;
};

/** Crisp unread count pill for the header bell. */
export function NotificationBellBadge({ count }: NotificationBellBadgeProps) {
  if (!count || count < 1) return null;
  const label = count > 99 ? '99+' : String(count);
  return (
    <span className={styles.badge} aria-label={`${count} unread notifications`}>
      {label}
    </span>
  );
}

export default NotificationBellIcon;
